/**
 * Workspace Selector Component
 * 
 * Dropdown selector for switching between workspaces.
 * Shows in the header/sidebar for quick workspace switching.
 * Uses fixed positioning to avoid overflow clipping issues.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { WorkspaceType, WORKSPACE_TYPE_LABELS, WORKSPACE_ROLE_LABELS } from '@budgira/shared';
import { cn } from '../../utils/cn';

interface WorkspaceSelectorProps {
  variant?: 'default' | 'compact';
  className?: string;
}

// Workspace type icons/emojis
const WORKSPACE_TYPE_ICONS: Record<WorkspaceType, string> = {
  personal: '👤',
  business: '💼',
  association: '🤝',
  other: '📁',
};

interface DropdownPosition {
  top: number;
  left: number;
}

export const WorkspaceSelector = ({ variant = 'default', className }: WorkspaceSelectorProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition>({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const { workspaces, currentWorkspace, switchWorkspace, fetchWorkspaces, isLoading } = useWorkspaceStore();
  
  const language = i18n.language as 'fr' | 'en';

  // Fetch workspaces on mount
  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  // Calculate dropdown position when opening
  const updateDropdownPosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 288; // w-72 = 18rem = 288px
      const windowWidth = window.innerWidth;
      
      // Calculate left position, ensuring dropdown doesn't go off-screen
      let left = rect.left;
      if (left + dropdownWidth > windowWidth - 8) {
        // Align to right edge with 8px margin
        left = windowWidth - dropdownWidth - 8;
      }
      if (left < 8) {
        left = 8; // Minimum 8px from left edge
      }
      
      setDropdownPosition({
        top: rect.bottom + 4, // 4px gap below button
        left: left,
      });
    }
  }, []);

  // Update position when opening and on scroll/resize
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      
      const handleScroll = () => updateDropdownPosition();
      const handleResize = () => updateDropdownPosition();
      
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen, updateDropdownPosition]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSwitch = async (workspaceId: string) => {
    if (workspaceId === currentWorkspace?.id) {
      setIsOpen(false);
      return;
    }
    
    // Navigate to dashboard first before switching workspace
    navigate('/');
    
    const success = await switchWorkspace(workspaceId);
    if (success) {
      setIsOpen(false);
      // Reload the page to refresh all data for the new workspace
      window.location.reload();
    }
  };

  // Don't render if no workspaces yet
  if (!currentWorkspace && !isLoading) {
    return null;
  }

  const getWorkspaceIcon = (workspace: { type: WorkspaceType; logoUrl?: string }) => {
    if (workspace.logoUrl) {
      // If it's an emoji (single character or emoji)
      if (workspace.logoUrl.length <= 4 && /\p{Emoji}/u.test(workspace.logoUrl)) {
        return workspace.logoUrl;
      }
      // Otherwise it's a URL - we'd show an image
      return null;
    }
    return WORKSPACE_TYPE_ICONS[workspace.type];
  };

  const getWorkspaceTypeLabel = (type: WorkspaceType) => {
    return WORKSPACE_TYPE_LABELS[type][language];
  };

  const getRoleLabel = (role: string) => {
    return WORKSPACE_ROLE_LABELS[role as keyof typeof WORKSPACE_ROLE_LABELS]?.[language] || role;
  };

  // Dropdown content (used in both variants)
  const dropdownContent = (
    <div
      ref={dropdownRef}
      className="fixed w-[calc(100vw-16px)] sm:w-72 max-w-72 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 py-1 z-[9999]"
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
      }}
    >
      {/* Current workspace header */}
      <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-700">
        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
          {t('workspaces.current')}
        </p>
      </div>

      {/* Workspace list */}
      <div className="max-h-64 overflow-y-auto">
        {workspaces.map((workspace) => (
          <button
            key={workspace.id}
            onClick={() => handleSwitch(workspace.id)}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-left',
              workspace.id === currentWorkspace?.id && 'bg-violet-50 dark:bg-violet-900/30'
            )}
          >
            <span className="text-xl flex-shrink-0">
              {getWorkspaceIcon(workspace)}
            </span>
            <div className="flex-1 min-w-0">
              <p className={cn(
                'text-sm font-medium truncate',
                workspace.id === currentWorkspace?.id
                  ? 'text-violet-700 dark:text-violet-300'
                  : 'text-slate-900 dark:text-slate-100'
              )}>
                {workspace.displayName || workspace.name}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {getWorkspaceTypeLabel(workspace.type)} • {getRoleLabel(workspace.role)}
              </p>
            </div>
            {workspace.id === currentWorkspace?.id && (
              <CheckIcon className="w-4 h-4 text-violet-600 dark:text-violet-400 flex-shrink-0" />
            )}
          </button>
        ))}
      </div>

      {/* Actions */}
      <div className="border-t border-slate-200 dark:border-slate-700 px-4 py-2">
        <Link
          to="/settings?tab=workspaces"
          onClick={() => setIsOpen(false)}
          className="flex items-center gap-2 text-sm text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300"
        >
          <SettingsIcon className="w-4 h-4" />
          {t('workspaces.manage')}
        </Link>
      </div>
    </div>
  );

  if (variant === 'compact') {
    return (
      <div className={cn('relative', className)}>
        <button
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          disabled={isLoading}
          className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors min-h-[40px] sm:min-h-[44px]"
        >
          <span className="text-lg">
            {currentWorkspace ? getWorkspaceIcon(currentWorkspace) : '📁'}
          </span>
          <span className="max-w-[120px] truncate hidden sm:inline">
            {currentWorkspace?.displayName || currentWorkspace?.name || t('workspaces.loading')}
          </span>
          <ChevronDownIcon className={cn(
            'w-4 h-4 text-slate-400 transition-transform',
            isOpen && 'rotate-180'
          )} />
        </button>

        {isOpen && createPortal(dropdownContent, document.body)}
      </div>
    );
  }

  // Default variant - larger display
  return (
    <div className={cn('relative', className)}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className="flex items-center gap-3 px-4 py-3 w-full text-left bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg hover:border-violet-300 dark:hover:border-violet-600 transition-colors"
      >
        <span className="text-2xl">
          {currentWorkspace ? getWorkspaceIcon(currentWorkspace) : '📁'}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
            {currentWorkspace?.displayName || currentWorkspace?.name || t('workspaces.loading')}
          </p>
          {currentWorkspace && (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {getWorkspaceTypeLabel(currentWorkspace.type)}
            </p>
          )}
        </div>
        <ChevronDownIcon className={cn(
          'w-5 h-5 text-slate-400 transition-transform',
          isOpen && 'rotate-180'
        )} />
      </button>

      {isOpen && createPortal(dropdownContent, document.body)}
    </div>
  );
};

// Icons
const ChevronDownIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const CheckIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const SettingsIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

export default WorkspaceSelector;
